import {AfterViewInit, Component, Inject} from '@angular/core';
import {PeoService} from '../../../../service/peo.service';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle
} from '@angular/material/dialog';
import {PeoApiModel} from '../../../../model/peo-api.model';
import {MatButton} from '@angular/material/button';
import {MatError, MatFormField, MatHint, MatLabel} from '@angular/material/form-field';
import {MatIcon} from '@angular/material/icon';
import {MatInput} from '@angular/material/input';
import {MatTooltip} from '@angular/material/tooltip';
import {NgIf} from '@angular/common';
import {FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {UtilityService} from '../../../../service/utility.service';
import {MatSnackBar} from '@angular/material/snack-bar';

/**
 * @class
 * @description This class is responsible for rendering the PEO Dialog component.
 * @export
 * @name PeoAdminDialogComponent
 */
@Component({
  imports: [
    MatButton,
    MatDialogActions,
    MatDialogContent,
    MatDialogTitle,
    MatError,
    MatFormField,
    MatHint,
    MatIcon,
    MatInput,
    MatLabel,
    MatTooltip,
    NgIf,
    ReactiveFormsModule
  ],
  selector: 'app-dialog-admin-peo',
  standalone: true,
  styleUrl: './peo.component.scss',
  templateUrl: './peo.component.html'
})
export class PeoAdminDialogComponent implements AfterViewInit {

  /**
   * @description This property contains the form group for the PEO.
   * @memberof PeoAdminDialogComponent
   * @name PeoAdminDialogComponent#form
   * @property
   * @protected
   * @type {FormGroup<{
   *       created: FormControl<Date>,
   *       name: FormControl<string>,
   *       peoId: FormControl<string>,
   *       updated: FormControl<Date>,
   *   }>}
   */
  protected readonly form: FormGroup<{
    created: FormControl<Date>,
    name: FormControl<string>,
    peoId: FormControl<string>,
    updated: FormControl<Date>
  }>;

  /**
   * @default true
   * @description This property contains the form disabled status.
   * @memberof PeoAdminDialogComponent
   * @name PeoAdminDialogComponent#formDisabled
   * @property
   * @protected
   * @type {boolean}
   */
  protected formDisabled: boolean = true;

  /**
   * @description This property contains the PEO model.
   * @memberof PeoAdminDialogComponent
   * @name PeoAdminDialogComponent#peo
   * @property
   * @protected
   * @type {PeoApiModel}
   */
  protected peo: PeoApiModel;

  /**
   * @constructor
   * @description This method instantiates a new instance of our PEO Dialog component.
   * @memberof PeoAdminDialogComponent
   * @name PeoAdminDialogComponent#constructor()
   * @param {MatDialogRef<PeoAdminDialogComponent>} dialogReference The injected instance of our dialog reference.
   * @param {FormBuilder} formBuilder The injected instance of our form builder.
   * @param {PeoService} service The injected instance of our PEO service provider.
   * @param {MatSnackBar} snackbar The injected instance of our snack bar service.
   * @param {{ peo?: PeoApiModel }} data The data for the dialog.
   * @public
   */
  public constructor(
    /**
     * @description This property contains the dialog reference.
     * @memberof PeoAdminDialogComponent
     * @name PeoAdminDialogComponent#dialogReference
     * @param {MatDialogRef<PeoAdminDialogComponent>} dialogReference The injected instance of our dialog reference.
     * @private
     * @property
     * @readonly
     * @type {MatDialogRef<PeoAdminDialogComponent>}
     */
    private readonly dialogReference: MatDialogRef<PeoAdminDialogComponent>,
    /**
     * @description This property contains the form builder.
     * @memberof PeoAdminDialogComponent
     * @name PeoAdminDialogComponent#formBuilder
     * @property
     * @protected
     * @readonly
     * @type {FormBuilder}
     */
    private readonly formBuilder: FormBuilder,
    /**
     * @description This property contains the PEO service provider.
     * @memberof PeoAdminDialogComponent
     * @name PeoAdminDialogComponent#service
     * @private
     * @property
     * @readonly
     * @type {PeoService}
     */
    private readonly service: PeoService,
    /**
     * @description This property contains the snack bar service.
     * @memberof PeoAdminDialogComponent
     * @name PeoAdminDialogComponent#snackbar
     * @private
     * @property
     * @readonly
     * @type {MatSnackBar}
     */
    private readonly snackbar: MatSnackBar,
    /**
     * @description The data for the dialog.
     * @memberof PeoAdminDialogComponent
     * @name PeoAdminDialogComponent#data
     * @property
     * @public
     * @type {{ peo?: PeoApiModel }}
     */
    @Inject(MAT_DIALOG_DATA)
    public data: { peo?: PeoApiModel }
  ) {

    // Check if we have a PEO model in the data.
    this.peo = this.data.peo ?? new PeoApiModel();

    // Build our form and set it into the instance.
    this.form = this.formBuilder.group({

      // Define our created form control.
      created: new FormControl<Date>(UtilityService.DateTimeUtc(), {

        // Set the form control to be non-nullable.
        nonNullable: true,

        // Add our required validator to the form control.
        validators: [Validators.required]
      }),

      // Define our name form control.
      name: new FormControl<string>(UtilityService.EmptyString, {

        // Set the form control to be non-nullable.
        nonNullable: true,

        // Add our required validator to the form control.
        validators: [Validators.required]
      }),

      // Define our PEO ID form control.
      peoId: new FormControl<string>(UtilityService.EmptyString, {

        // Set the form control to be non-nullable.
        nonNullable: true,

        // Add our required validator to the form control.
        validators: [Validators.required]
      }),

      // Define our updated form control.
      updated: new FormControl<Date>(UtilityService.DateTimeUtc(), {

        // Set the form control to be non-nullable.
        nonNullable: true,

        // Add our required validator to the form control.
        validators: [Validators.required]
      })
    });
  }

  /**
   * @description This method closes the open dialog.
   * @memberof PeoAdminDialogComponent
   * @method
   * @name PeoAdminDialogComponent#cancel()
   * @protected
   * @returns {void}
   */
  protected cancel(): void {

    // Close the open dialog.
    this.dialogReference.close();
  }

  /**
   * @description This method converts the form to its model variant.
   * @memberof PeoAdminDialogComponent
   * @method
   * @name PeoAdminDialogComponent#formToModel()
   * @private
   * @returns {PeoApiModel}
   */
  private formToModel(): PeoApiModel {

    // Define our model instance from the form and return it.
    return this.form.value as PeoApiModel;
  }

  /**
   * @description This method resets the PEO form to the original values.
   * @memberof PeoAdminDialogComponent
   * @method
   * @name PeoAdminDialogComponent#reset()
   * @protected
   * @returns {void}
   */
  protected reset(): void {

    // Disable the form if it is not already disabled.
    if (!this.formDisabled) {

      // Disable the form.
      this.formDisabled = true;
    }

    // Set the administrator information into the form.
    this.form.patchValue({

      // Patch the created date into the form.
      created: this.peo.created,

      // Patch the name into the form.
      name: this.peo.name,

      // Patch the PEO ID into the form.
      peoId: this.peo.peoId,

      // Patch the updated date into the form.
      updated: this.peo.updated,
    });

    // Re-enable the form if it was disabled.
    if (this.formDisabled) this.formDisabled = false;
  }

  /**
   * @description This method saves the PEO model.
   * @memberof PeoAdminDialogComponent
   * @method
   * @name PeoAdminDialogComponent#save()
   * @protected
   * @returns {void}
   */
  protected save(): void {

    // Disable the form while we're saving.
    this.formDisabled = true;

    // Save the PEO model.
    this.service.save(this.formToModel()).subscribe({

      // Handle any errors that occur during the save operation.
      error: (e: Error) => {

        // Show an error message.
        this.snackbar.open(`An error occurred while saving the PEO ${this.form.controls.peoId.value}:  ${e.message}`, 'Dismiss', {
          duration: 5000,
          horizontalPosition: 'end',
          panelClass: ['snackbar-error'],
          verticalPosition: 'bottom'
        });

        // Re-enable the form.
        this.formDisabled = false;
      },

      // Handle the completion of the save operation.
      next: (p: PeoApiModel) => {

        // Show a success message.
        this.snackbar.open(`PEO ${p.peoId} saved successfully.`, 'Dismiss', {
          duration: 5000,
          horizontalPosition: 'end',
          panelClass: ['snackbar-success'],
          verticalPosition: 'bottom'
        });

        // We're done, close the dialog.
        this.dialogReference.close(p);
      }
    })
  }

  /**
   * @abstract
   * @description This method is called after Angular has fully initialized the component's view.
   * @memberof PeoAdminDialogComponent
   * @name PeoAdminDialogComponent#ngAfterViewInit()
   * @method
   * @public
   * @returns {void}
   */
  public ngAfterViewInit(): void {

    // Reset the form.
    this.reset();
  }
}
